'use client';

import { useEffect } from 'react';
import { logAnalyticsEvent } from '@/lib/actions/analytics';

const SESSION_KEY = 'cheffo:session-logged';

/**
 * Logs one `session` analytics event per browser session (deduped via
 * sessionStorage). Drives visit frequency + last-active on the admin analytics
 * dashboard. Renders nothing.
 *
 * Mounted in BOTH the authenticated and the public layout (#881). It used to be
 * authenticated-only, which made `anonSessions30d` structurally always zero —
 * the dashboard had a figure for logged-out visits that no code path could ever
 * increment, so "nobody visits logged out" and "we never look" were the same
 * reading. Anonymous recipe views were being recorded the whole time, so the
 * gap was sessions specifically.
 *
 * `user_id` is resolved server-side in the action and is NULL for a logged-out
 * visitor. The dedup key is in sessionStorage, NOT localStorage, deliberately:
 * it dies with the tab, so it counts a visit without becoming a persistent
 * identifier for someone who never signed in. That also means anonymous
 * SESSIONS are countable but anonymous PEOPLE are not — two visits from one
 * stranger and one visit each from two are indistinguishable here, by design.
 */
export function SessionBeacon() {
  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // Private-mode / storage-disabled: fall through and log anyway.
    }
    void logAnalyticsEvent({ eventType: 'session', path: window.location.pathname });
  }, []);

  return null;
}
